/** Mock fixtures for the Ticker Intelligence Workspace mockup.
 *
 * Every value here is invented for layout review. Nothing in this file calls a
 * provider, a backend route or Mongo. Field names deliberately mirror the shapes
 * that already exist (scanner_cache technicals, regime_engine STATE_LABELS,
 * FundamentalsAdapter, intel_refresh shadow/talk docs) so a later separately
 * authorised release can swap the fixture for a real read without renaming the UI.
 */

import {
  BASKET, REASON, SCREEN, actionLayerOf, dataLayerOf, eventLayerOf, reasonForScreen, screenFromLayers,
} from "@/workspace/screenLanguage";

/** Only the four HMM labels the engine emits, plus the confirmed-state gate. */
export const REGIME_STATES = ["Quiet Range", "Bullish Trend", "Bearish Trend", "Volatile"];
export const CONFIRMED_TRANSITIONING = "transitioning";

export const WORKSPACE_TICKERS = ["ULTA", "AAPL", "AVAV"];

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SESSION_END = new Date(Date.UTC(2026, 8, 4));

function sessionsBack(count) {
  const out = [];
  const cur = new Date(SESSION_END.getTime());
  while (out.length < count) {
    const day = cur.getUTCDay();
    if (day !== 0 && day !== 6) out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() - 1);
  }
  return out.reverse();
}

function mean(xs) {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

function sma(closes, i, n) {
  if (i + 1 < n) return null;
  return mean(closes.slice(i + 1 - n, i + 1));
}

function stdev(xs) {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(mean(xs.map((x) => (x - m) ** 2)));
}

function rsi14(closes, i) {
  if (i < 14) return null;
  let up = 0;
  let down = 0;
  for (let k = i - 13; k <= i; k += 1) {
    const d = closes[k] - closes[k - 1];
    if (d >= 0) up += d;
    else down -= d;
  }
  if (down === 0) return 100;
  const rs = (up / 14) / (down / 14);
  return 100 - (100 / (1 + rs));
}

function ema(values, n) {
  const k = 2 / (n + 1);
  const out = [];
  values.forEach((v, i) => {
    out.push(i === 0 ? v : v * k + out[i - 1] * (1 - k));
  });
  return out;
}

/** Behaviour per regime, so the ribbon and the candles tell the same story. */
const REGIME_BEHAVIOUR = {
  // Range states mean-revert to the level they opened at; trend states drift
  // with no pull, so a band on the ribbon always matches the candles under it.
  "Quiet Range": { drift: 0, vol: 0.6, pull: 0.08 },
  "Bullish Trend": { drift: 0.0030, vol: 0.8, pull: 0 },
  "Bearish Trend": { drift: -0.0028, vol: 0.9, pull: 0 },
  Volatile: { drift: 0, vol: 2.4, pull: 0.03 },
};

/** Deterministic OHLCV + indicator + regime-band series. Layout fixture only.
 * Rescaled at the end so the final close equals the ticker's quoted price.
 */
function buildSeries({ seed, bars, start, vol, endPrice, regimePlan }) {
  const rng = mulberry32(seed);
  const dates = sessionsBack(bars);
  const closes = [];
  const rows = [];
  let px = start;
  let anchor = start;

  dates.forEach((date, i) => {
    const plan = regimePlan.find((p) => i >= p.from && i <= p.to);
    const b = REGIME_BEHAVIOUR[plan ? plan.state : "Quiet Range"];
    if (plan && i === plan.from) anchor = px;
    const shock = (rng() - 0.5) * 2 * vol * b.vol * px;
    px = Math.max(1, px + b.drift * px + shock + b.pull * (anchor - px));
    const open = i === 0 ? px : closes[i - 1];
    const high = Math.max(open, px) * (1 + rng() * vol * b.vol * 0.6);
    const low = Math.min(open, px) * (1 - rng() * vol * b.vol * 0.6);
    closes.push(px);
    rows.push({
      date,
      open,
      high,
      low,
      close: px,
      volume: Math.round(600000 + rng() * 2400000 * b.vol),
    });
  });

  // Price levels are linear, so one factor keeps candles, SMAs and bands aligned.
  const k = endPrice / closes[closes.length - 1];
  for (let i = 0; i < rows.length; i += 1) {
    closes[i] *= k;
    rows[i].open = +(rows[i].open * k).toFixed(2);
    rows[i].high = +(rows[i].high * k).toFixed(2);
    rows[i].low = +(rows[i].low * k).toFixed(2);
    rows[i].close = +(rows[i].close * k).toFixed(2);
  }

  const macdLine = ema(closes, 12).map((v, i) => v - ema(closes, 26)[i]);
  const signalLine = ema(macdLine, 9);
  const ema9 = ema(closes, 9);
  const ema20 = ema(closes, 20);

  return rows.map((r, i) => {
    const s20 = sma(closes, i, 20);
    const s50 = sma(closes, i, 50);
    const s200 = sma(closes, i, 200);
    const win = closes.slice(Math.max(0, i - 19), i + 1);
    const sd = stdev(win);
    const plan = regimePlan.find((p) => i >= p.from && i <= p.to);
    let atr = null;
    if (i >= 14) {
      let s = 0;
      for (let k = i - 13; k <= i; k += 1) {
        const prev = rows[k - 1].close;
        const tr = Math.max(rows[k].high - rows[k].low, Math.abs(rows[k].high - prev), Math.abs(rows[k].low - prev));
        s += tr;
      }
      atr = +(s / 14).toFixed(2);
    }
    return {
      ...r,
      sma20: s20 == null ? null : +s20.toFixed(2),
      sma50: s50 == null ? null : +s50.toFixed(2),
      sma200: s200 == null ? null : +s200.toFixed(2),
      ema9: i >= 8 ? +ema9[i].toFixed(2) : null,
      ema20: i >= 19 ? +ema20[i].toFixed(2) : null,
      bbUpper: s20 == null ? null : +(s20 + 2 * sd).toFixed(2),
      bbLower: s20 == null ? null : +(s20 - 2 * sd).toFixed(2),
      rsi14: rsi14(closes, i) == null ? null : +rsi14(closes, i).toFixed(1),
      atr14: atr,
      macdHistogram: +(macdLine[i] - signalLine[i]).toFixed(3),
      regimeState: plan ? plan.state : "Quiet Range",
    };
  }).map((r, i, all) => {
    // Confirmed label is a fixture majority over the last five mock bars —
    // not a live HMM calculation. Labeled as such in the chart legend.
    const window = all.slice(Math.max(0, i - 4), i + 1).map((x) => x.regimeState);
    const counts = {};
    window.forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const confirmed = ranked[0] && ranked[0][1] >= 3 ? ranked[0][0] : "transitioning";
    const trend = r.close > (r.sma50 ?? r.close)
      ? ((r.macdHistogram ?? 0) >= 0 ? "bullish" : "sideways")
      : ((r.macdHistogram ?? 0) < 0 ? "bearish" : "sideways");
    return {
      ...r,
      confirmedRegimeState: confirmed,
      regimeConfidence: confirmed === r.regimeState ? +(0.62 + Math.min(0.26, window.length * 0.04)).toFixed(2) : 0.54,
      trendState: trend,
    };
  });
}

/** Header quote is read off the series so the number and the chart agree. */
function quoteFrom(series) {
  const n = series.length;
  const close = series[n - 1].close;
  const prev = series[n - 2].close;
  return {
    price: close,
    change: +(close - prev).toFixed(2),
    changePct: +(((close / prev) - 1) * 100).toFixed(2),
  };
}

/** Relative strength vs SPY and the sector ETF, rebased to 100. */
function buildRelativeStrength(series, seed) {
  const rng = mulberry32(seed);
  const base = series[0].close;
  let spy = 100;
  let sector = 100;
  return series
    .filter((_, i) => i % 3 === 0)
    .map((r) => {
      spy += (rng() - 0.48) * 0.9;
      sector += (rng() - 0.5) * 1.1;
      return {
        date: r.date,
        ticker: +((r.close / base) * 100).toFixed(2),
        spy: +spy.toFixed(2),
        sector: +sector.toFixed(2),
      };
    });
}

const ULTA_SERIES = buildSeries({
  seed: 11,
  bars: 180,
  start: 402,
  vol: 0.011,
  endPrice: 431.62,
  regimePlan: [
    { from: 0, to: 54, state: "Bearish Trend" },
    { from: 55, to: 118, state: "Quiet Range" },
    { from: 119, to: 148, state: "Bullish Trend" },
    { from: 149, to: 179, state: "Quiet Range" },
  ],
});

(function pinUltaDailyEntry(series) {
  for (let i = series.length - 12; i < series.length; i += 1) {
    const r = series[i];
    if (r.ema20 != null) r.ema9 = +(r.ema20 - 1.15 - (series.length - 1 - i) * 0.04).toFixed(2);
  }
  const last = series[series.length - 1];
  if (last.bbLower != null && last.bbUpper != null) {
    last.close = +(last.bbLower + 0.16 * (last.bbUpper - last.bbLower)).toFixed(2);
    last.open = +(last.close + 0.42).toFixed(2);
    last.low = +(last.close - 0.38).toFixed(2);
    last.high = +(last.open + 0.28).toFixed(2);
  }
  last.rsi14 = 36.4;
})(ULTA_SERIES);

const AAPL_SERIES = buildSeries({
  seed: 23,
  bars: 180,
  start: 214,
  vol: 0.010,
  endPrice: 241.08,
  regimePlan: [
    { from: 0, to: 48, state: "Quiet Range" },
    { from: 49, to: 118, state: "Bullish Trend" },
    { from: 119, to: 151, state: "Quiet Range" },
    { from: 152, to: 179, state: "Bullish Trend" },
  ],
});

const AVAV_SERIES = buildSeries({
  seed: 37,
  bars: 180,
  start: 168,
  vol: 0.012,
  endPrice: 188.44,
  regimePlan: [
    { from: 0, to: 38, state: "Bullish Trend" },
    { from: 39, to: 94, state: "Volatile" },
    { from: 95, to: 134, state: "Bearish Trend" },
    { from: 135, to: 179, state: "Volatile" },
  ],
});

/** Strategy families exactly as the product direction names them. */
const STRATEGY_FAMILIES = [
  {
    id: "income",
    label: "Income",
    blurb: "Premium collection against cash or stock.",
    strategies: ["Cash-secured put", "Covered call", "Bull put spread", "Iron condor"],
  },
  {
    id: "directional",
    label: "Defined-risk directional",
    blurb: "Capped-risk expressions of a direction.",
    strategies: ["Call spread", "Put spread", "Diagonal"],
  },
  {
    id: "protection",
    label: "Protection / hedging",
    blurb: "Downside cover on an existing holding.",
    strategies: ["Protective put", "Collar", "Put spread"],
  },
  {
    id: "volatility",
    label: "Volatility",
    blurb: "Positions on the variance, not the direction.",
    strategies: ["Straddle", "Strangle", "Calendar", "Butterfly"],
  },
  {
    id: "repair",
    label: "Position repair",
    blurb: "Adjustments to an position already on.",
    strategies: ["Roll out", "Covered-call overlay", "Collar", "Defined-risk adjustment"],
  },
];

export function strategyFamilies() {
  return STRATEGY_FAMILIES;
}

const ULTA = {
  ticker: "ULTA",
  companyName: "Ulta Beauty, Inc.",
  sector: "Consumer Discretionary",
  industry: "Specialty Retail",
  ...quoteFrom(ULTA_SERIES),
  quoteQuality: "observed",
  quoteQualityLabel: "Observed quote",
  regime: {
    state: "Quiet Range",
    confidence: 0.78,
    confirmedState: "Quiet Range",
    instantState: "Quiet Range",
    disagreement: false,
    sojournDays: 14,
    sojournP75: 22,
    ageBars: 11,
  },
  technicalScore: 6.8,
  fundamentalScore: 7.4,
  earnings: {
    date: "2026-09-18",
    quality: "confirmed",
    daysTo: 12,
  },
  optionsState: "observed",
  decisionState: "flag",
  decisionSummary:
    "Range-bound with a confirmed Quiet Range and an observed options quote. A confirmed earnings event falls inside the selected holding window, so the income screen shows a data or event flag.",
  premium: {
    ivContext: "low",
    label: "Low IV — thin premium environment",
    expectedMovePct: 4.1,
    expectedMoveNote: "30-day expected move ±4.1%",
  },
  keyRisks: [
    { level: "caution", text: "Confirmed earnings event falls inside selected time window." },
    { level: "info", text: "Range top near 448 has held three times since July." },
  ],
  blockers: [],
  series: ULTA_SERIES,
  relativeStrength: buildRelativeStrength(ULTA_SERIES, 101),
};

const AAPL = {
  ticker: "AAPL",
  companyName: "Apple Inc.",
  sector: "Information Technology",
  industry: "Technology Hardware, Storage & Peripherals",
  ...quoteFrom(AAPL_SERIES),
  quoteQuality: "partial",
  quoteQualityLabel: "Required quote fields unavailable",
  regime: {
    state: "Bullish Trend",
    confidence: 0.61,
    confirmedState: "Quiet Range",
    instantState: "Bullish Trend",
    disagreement: true,
    sojournDays: 4,
    sojournP75: 19,
    ageBars: 4,
  },
  technicalScore: 7.9,
  fundamentalScore: 8.1,
  earnings: {
    date: "2026-10-29",
    quality: "expected",
    daysTo: 53,
  },
  optionsState: "partial",
  decisionState: "flag",
  decisionSummary:
    "Instant regime reads bullish but the five-day confirmed state is still Quiet Range. Required quote fields are unavailable on at least one listed leg.",
  premium: {
    ivContext: "typical",
    label: "Typical IV — ordinary premium environment",
    expectedMovePct: 5.4,
    expectedMoveNote: "30-day expected move ±5.4%",
  },
  keyRisks: [
    { level: "caution", text: "Instant and five-day confirmed regimes disagree. Ranked strategies follow the confirmed path." },
    { level: "caution", text: "Required quote fields are unavailable on the near expiry." },
  ],
  blockers: [],
  series: AAPL_SERIES,
  relativeStrength: buildRelativeStrength(AAPL_SERIES, 202),
};

const AVAV = {
  ticker: "AVAV",
  companyName: "AeroVironment, Inc.",
  sector: "Industrials",
  industry: "Aerospace & Defense",
  ...quoteFrom(AVAV_SERIES),
  quoteQuality: "unverified",
  quoteQualityLabel: "Option quote timestamp unavailable",
  regime: {
    state: "Volatile",
    confidence: 0.83,
    confirmedState: CONFIRMED_TRANSITIONING,
    instantState: "Volatile",
    disagreement: true,
    sojournDays: 9,
    sojournP75: 12,
    ageBars: 9,
  },
  technicalScore: 3.4,
  fundamentalScore: 5.2,
  earnings: {
    date: "2026-12-03",
    quality: "predicted",
    daysTo: 88,
  },
  optionsState: "unverified",
  decisionState: "unavailable",
  decisionSummary:
    "Confirmed regime is transitioning and the option quote timestamp is unavailable. Basket action is unavailable until required quote fields and a timestamp are present.",
  premium: {
    ivContext: "elevated",
    label: "Elevated IV — rich but unstable premium",
    expectedMovePct: 11.8,
    expectedMoveNote: "30-day expected move ±11.8%",
  },
  keyRisks: [
    { level: "info", text: "Realised volatility is roughly double the sector median." },
  ],
  blockers: [
    { level: "blocking", text: "Option quote timestamp is unavailable." },
    { level: "blocking", text: "Confirmed regime is transitioning. No three-of-five majority in the last five sessions." },
  ],
  series: AVAV_SERIES,
  relativeStrength: buildRelativeStrength(AVAV_SERIES, 303),
};

const BY_TICKER = { ULTA, AAPL, AVAV };

/* ---------------------------------------------------------------- technicals */

/** Inputs the series cannot produce on its own. Illustrative values. */
const TECHNICAL_STATIC = {
  ULTA: {
    adx: { reading: "17.4 · weak trend", contribution: 0.4 },
    volume: { reading: "0.91× the 20-day average", contribution: -0.4 },
    rs: { reading: "+3.2 pts vs SPY (60d)", contribution: 0.6 },
  },
  AAPL: {
    adx: { reading: "26.8 · trending", contribution: 1.0 },
    volume: { reading: "1.14× the 20-day average", contribution: 0.4 },
    rs: { reading: "+7.9 pts vs SPY (60d)", contribution: 0.9 },
  },
  AVAV: {
    adx: { reading: "31.2 · trending", contribution: 0.6 },
    volume: { reading: "2.38× the 20-day average", contribution: 0.3 },
    rs: { reading: "−11.4 pts vs SPY (60d)", contribution: -0.9 },
  },
};

function clamp(v, lo = -2, hi = 2) {
  return +Math.max(lo, Math.min(hi, v)).toFixed(1);
}

function signed(v, digits = 1, suffix = "") {
  const s = v >= 0 ? "+" : "−";
  return `${s}${Math.abs(v).toFixed(digits)}${suffix}`;
}

/** Derived from the same bars the Technicals charts draw, so readings agree. */
export function technicalInputs(ticker) {
  const t = BY_TICKER[ticker];
  if (!t) return [];
  const s = TECHNICAL_STATIC[ticker] || TECHNICAL_STATIC.ULTA;
  const last = t.series[t.series.length - 1];
  const vs50 = ((last.close / last.sma50) - 1) * 100;
  const vs200 = last.sma200 ? ((last.close / last.sma200) - 1) * 100 : 0;
  const bbPos = last.bbUpper && last.bbLower
    ? (last.close - last.bbLower) / (last.bbUpper - last.bbLower)
    : 0.5;
  return [
    { input: "Close vs SMA50", reading: signed(vs50, 1, "%"), contribution: clamp(vs50 / 3) },
    {
      input: "Close vs SMA200",
      reading: last.sma200 ? signed(vs200, 1, "%") : "Needs 200 prior bars",
      contribution: last.sma200 ? clamp(vs200 / 6) : 0,
    },
    { input: "RSI (14)", reading: last.rsi14?.toFixed(1) ?? "—", contribution: clamp(((last.rsi14 ?? 50) - 50) / 12) },
    { input: "MACD histogram", reading: signed(last.macdHistogram, 2), contribution: clamp(last.macdHistogram / (last.close * 0.006)) },
    { input: "ADX (14)", reading: s.adx.reading, contribution: s.adx.contribution },
    { input: "Bollinger position", reading: `${bbPos.toFixed(2)} of band`, contribution: clamp((bbPos - 0.5) * 2) },
    { input: "Volume vs 20d", reading: s.volume.reading, contribution: s.volume.contribution },
    { input: "Relative strength", reading: s.rs.reading, contribution: s.rs.contribution },
  ];
}

function heldBars(fast, slow) {
  const n = fast.length - 1;
  if (fast[n] == null || slow[n] == null) return null;
  const nowAbove = fast[n] > slow[n];
  let days = 0;
  for (let i = n; i >= 0; i -= 1) {
    if (fast[i] == null || slow[i] == null) break;
    if ((fast[i] > slow[i]) !== nowAbove) break;
    days += 1;
  }
  return days;
}

function emaPairState(fast, slow, pairLabel, held) {
  if (fast == null || slow == null) {
    return { state: "neutral", line: `${pairLabel} unavailable`, tone: "neutral", held: null };
  }
  const above = fast > slow;
  if (held != null && held <= 1) {
    return {
      state: above ? "crossing up" : "crossing down",
      line: `${pairLabel} ${above ? "crossing up" : "crossing down"}`,
      tone: above ? "constructive" : "weakening",
      held,
    };
  }
  return {
    state: above ? `${pairLabel.split("/")[0]} above ${pairLabel.split("/")[1]}` : `${pairLabel.split("/")[0]} below ${pairLabel.split("/")[1]}`,
    line: above
      ? `${pairLabel.replace("/", " EMA above ")} EMA · constructive`
      : `${pairLabel.replace("/", " EMA below ")} EMA · weakening`,
    tone: above ? "constructive" : "weakening",
    held,
  };
}

/** Weekly OHLC from sequential daily bars. Fixture aggregation only. */
export function weeklyBars(series) {
  const out = [];
  for (let i = 0; i < series.length; i += 5) {
    const chunk = series.slice(i, i + 5);
    const closes = chunk.map((r) => r.close);
    out.push({
      date: chunk[chunk.length - 1].date,
      open: chunk[0].open,
      high: Math.max(...chunk.map((r) => r.high)),
      low: Math.min(...chunk.map((r) => r.low)),
      close: chunk[chunk.length - 1].close,
      volume: chunk.reduce((a, r) => a + (r.volume || 0), 0),
    });
    const idx = out.length - 1;
    const allCloses = out.map((r) => r.close);
    out[idx].sma20 = sma(allCloses, idx, 20);
    out[idx].sma50 = sma(allCloses, idx, 50);
    out[idx].sma200 = null;
    const e10 = ema(allCloses, 10);
    const e20 = ema(allCloses, 20);
    out[idx].ema10 = idx >= 9 ? +e10[idx].toFixed(2) : null;
    out[idx].ema20 = idx >= 19 ? +e20[idx].toFixed(2) : null;
    out[idx].ema9 = out[idx].ema10;
    const sd = stdev(closes);
    const s20 = out[idx].sma20;
    out[idx].bbUpper = s20 == null ? null : +(s20 + 2 * sd).toFixed(2);
    out[idx].bbLower = s20 == null ? null : +(s20 - 2 * sd).toFixed(2);
    out[idx].rsi14 = null;
    out[idx].macdHistogram = 0;
  }
  return out;
}

/** Keep weekly 10/20 EMA presentation aligned with the ULTA fixture copy. */
export function pinWeeklyEmaForEntry(ticker, weekly) {
  if (ticker !== "ULTA" || !weekly?.length) return weekly;
  for (let i = Math.max(0, weekly.length - 6); i < weekly.length; i += 1) {
    const r = weekly[i];
    if (r.ema20 != null) r.ema10 = +(r.ema20 + 1.05 + (weekly.length - 1 - i) * 0.03).toFixed(2);
  }
  return weekly;
}

/**
 * Analytical income-entry context. Descriptive only and does not override
 * earnings, quote quality, liquidity, selection or strategy validation.
 */
export function technicalEntryContext(ticker) {
  const t = BY_TICKER[ticker];
  if (!t) return null;
  const series = t.series;
  const last = series[series.length - 1];
  const ema9s = series.map((r) => r.ema9);
  const ema20s = series.map((r) => r.ema20);
  const dailyHeld = heldBars(ema9s, ema20s);
  const daily = emaPairState(last.ema9, last.ema20, "9/20", dailyHeld);
  daily.line = last.ema9 == null || last.ema20 == null
    ? "Daily 9/20 EMA unavailable"
    : last.ema9 >= last.ema20
      ? (dailyHeld <= 1 ? "9 EMA crossing above 20 EMA" : "9 EMA above 20 EMA · constructive")
      : (dailyHeld <= 1 ? "9 EMA crossing below 20 EMA" : "9 EMA below 20 EMA · weakening");

  const weekly = pinWeeklyEmaForEntry(ticker, weeklyBars(series));
  const wLast = weekly[weekly.length - 1];
  const wFast = weekly.map((r) => r.ema10);
  const wSlow = weekly.map((r) => r.ema20);
  const weeklyHeld = heldBars(wFast, wSlow);
  const weeklyState = emaPairState(wLast?.ema10, wLast?.ema20, "10/20", weeklyHeld);
  weeklyState.line = wLast?.ema10 == null || wLast?.ema20 == null
    ? "Weekly 10/20 EMA unavailable"
    : wLast.ema10 >= wLast.ema20
      ? (weeklyHeld <= 1 ? "10 EMA crossing above 20 EMA" : "10 EMA above 20 EMA · constructive")
      : (weeklyHeld <= 1 ? "10 EMA crossing below 20 EMA" : "10 EMA below 20 EMA · weakening");

  const bbSpan = last.bbUpper != null && last.bbLower != null ? last.bbUpper - last.bbLower : null;
  const bbPct = bbSpan ? (last.close - last.bbLower) / bbSpan : null;
  const distLower = last.bbLower != null ? ((last.close - last.bbLower) / last.close) * 100 : null;
  const distUpper = last.bbUpper != null ? ((last.bbUpper - last.close) / last.close) * 100 : null;
  let rangeLabel = "Mid-band";
  if (bbPct != null && bbPct <= 0.25) rangeLabel = "Near lower Bollinger Band";
  else if (bbPct != null && bbPct >= 0.75) rangeLabel = "Near upper Bollinger Band";

  const rs = t.relativeStrength[t.relativeStrength.length - 1];
  const vsSpy = rs ? +(rs.ticker - rs.spy).toFixed(1) : null;
  const vsSector = rs ? +(rs.ticker - rs.sector).toFixed(1) : null;

  const quiet = t.regime.confirmedState === "Quiet Range";
  const meanRev = quiet && bbPct != null && bbPct <= 0.30;
  const trendAligned = daily.tone === "constructive" && weeklyState.tone === "constructive"
    && (t.regime.confirmedState === "Bullish Trend" || t.regime.confirmedState === "Bearish Trend");
  const trendAdverse = daily.tone !== weeklyState.tone && !quiet;
  let tag = "Mixed";
  if (meanRev) tag = "Mean-reversion context";
  else if (trendAligned) tag = "Trend-aligned";
  else if (trendAdverse || t.regime.confirmedState === "Volatile") tag = "Trend-adverse";

  const premium = t.premium || { ivContext: "typical", label: "IV context unavailable" };

  if (ticker === "ULTA") {
    daily.line = "9 EMA below 20 EMA · weakening";
    daily.tone = "weakening";
    weeklyState.line = "10 EMA above 20 EMA · constructive";
    weeklyState.tone = "constructive";
    rangeLabel = "Near lower Bollinger Band";
    tag = "Mean-reversion context";
  }

  let decision;
  if (tag === "Mean-reversion context" && premium.ivContext === "low") {
    decision = "Mixed context — technical stretch supports mean-reversion research, but income compensation remains limited.";
  } else if (tag === "Trend-aligned") {
    decision = "Mixed context — trend relationships agree, but earnings, quotes and policy still describe selected-structure status.";
  } else if (tag === "Trend-adverse") {
    decision = "Mixed context — daily and weekly trend relationships disagree. Analytical context only.";
  } else {
    decision = "Mixed context — technicals, premium and regime do not all point the same way. Analytical context only.";
  }

  return {
    daily: {
      ema9: last.ema9,
      ema20: last.ema20,
      state: daily.state,
      line: daily.line,
      tone: daily.tone,
      daysSince: dailyHeld,
    },
    weekly: {
      ema10: wLast?.ema10 ?? null,
      ema20: wLast?.ema20 ?? null,
      state: weeklyState.state,
      line: weeklyState.line,
      tone: weeklyState.tone,
      daysSince: weeklyHeld,
      bars: weekly,
    },
    meanRev: {
      bbPct,
      bbPctLabel: bbPct == null ? "Not available in this output" : `${Math.round(bbPct * 100)}th percentile of the band`,
      distLower,
      distUpper,
      rangeLabel,
      rsi: last.rsi14,
      atr: last.atr14,
    },
    relative: {
      vsSpy,
      vsSector,
      spyLabel: vsSpy == null ? "Not available in this output" : `${vsSpy >= 0 ? "+" : "−"}${Math.abs(vsSpy).toFixed(1)} pts vs SPY`,
      sectorLabel: vsSector == null ? "Not available in this output" : `${vsSector >= 0 ? "+" : "−"}${Math.abs(vsSector).toFixed(1)} pts vs sector`,
    },
    regime: t.regime.confirmedState,
    premium,
    tag,
    decision,
  };
}

const LEVELS = {
  ULTA: {
    resistance: [{ level: 448.2, label: "Range top · 3 touches" }, { level: 462.0, label: "May swing high" }],
    support: [{ level: 418.5, label: "Range floor" }, { level: 396.4, label: "SMA200" }],
  },
  AAPL: {
    resistance: [{ level: 248.9, label: "All-time high" }],
    support: [{ level: 232.1, label: "SMA20" }, { level: 221.7, label: "Breakout retest" }],
  },
  AVAV: {
    resistance: [{ level: 205.6, label: "Gap fill" }, { level: 224.3, label: "Pre-drop shelf" }],
    support: [{ level: 178.0, label: "June low" }],
  },
};

export function keyLevels(ticker) {
  return LEVELS[ticker] || { resistance: [], support: [] };
}

/* ------------------------------------------------------------------- regime */

const REGIME_COMPATIBILITY = {
  "Quiet Range": [
    { family: "Income", verdict: "available", note: "Historical observations for premium-collection structures are present in this state." },
    { family: "Defined-risk directional", verdict: "limited", note: "Historical trend-following sample is limited in a range." },
    { family: "Protection / hedging", verdict: "limited", note: "Historical protection observations are sparse in this state." },
    { family: "Volatility", verdict: "differs", note: "Historical long-volatility observations differ from short realised-vol periods." },
  ],
  "Bullish Trend": [
    { family: "Income", verdict: "available", note: "Historical cash-secured put observations exist in this state." },
    { family: "Defined-risk directional", verdict: "available", note: "Historical call-spread observations exist in this state." },
    { family: "Protection / hedging", verdict: "limited", note: "Historical collar observations are limited while the trend persists." },
    { family: "Volatility", verdict: "differs", note: "Historical realised-variance observations differ in trend-following periods." },
  ],
  "Bearish Trend": [
    { family: "Income", verdict: "differs", note: "Historical put-selling observations differ from this confirmed state." },
    { family: "Defined-risk directional", verdict: "available", note: "Historical put-spread observations exist in this state." },
    { family: "Protection / hedging", verdict: "available", note: "Historical protection observations exist in this state." },
    { family: "Volatility", verdict: "limited", note: "Historical sample depends on whether IV had already repriced." },
  ],
  Volatile: [
    { family: "Income", verdict: "differs", note: "Historical premium-collection observations differ in this state." },
    { family: "Defined-risk directional", verdict: "limited", note: "Historical sample is limited to defined-risk structures." },
    { family: "Protection / hedging", verdict: "available", note: "Historical protection observations exist in this state." },
    { family: "Volatility", verdict: "available", note: "Historical direction-agnostic observations exist in this state." },
  ],
};

export function regimeCompatibility(state) {
  return REGIME_COMPATIBILITY[state] || REGIME_COMPATIBILITY["Quiet Range"];
}

export function regimeRibbon(ticker) {
  const t = BY_TICKER[ticker];
  if (!t) return [];
  const out = [];
  t.series.forEach((r) => {
    const last = out[out.length - 1];
    if (last && last.state === r.regimeState) last.to = r.date;
    else out.push({ state: r.regimeState, from: r.date, to: r.date });
  });
  return out;
}

/* ------------------------------------------------------- options / strategy */

/** Fixture as-of. DTE is computed from this date, never from the wall clock. */
export const MOCK_AS_OF = "2026-09-04";

/** Wheel income policy window. Visual band only — no live policy call. */
export const POLICY_WINDOW = { min: 35, max: 45, label: "Policy target 35–45 DTE" };

export const EARNINGS_CONFLICT = {
  clear: { state: "clear", label: "No confirmed earnings event in selected time window" },
  inside: { state: "inside", label: "Confirmed earnings event falls inside selected time window" },
  predicted: { state: "predicted", label: "Earnings date is predicted, not confirmed" },
  unavailable: { state: "unavailable", label: "Earnings date unavailable" },
};

const LISTED_EXPIRIES = [
  { date: "2026-09-11", dte: 7, monthly: false, friday: true },
  { date: "2026-09-18", dte: 14, monthly: false, friday: true },
  { date: "2026-09-25", dte: 21, monthly: false, friday: true },
  { date: "2026-10-02", dte: 28, monthly: false, friday: true },
  { date: "2026-10-09", dte: 35, monthly: false, friday: true },
  { date: "2026-10-16", dte: 42, monthly: true, friday: true },
  { date: "2026-10-23", dte: 49, monthly: false, friday: true },
  { date: "2026-11-20", dte: 77, monthly: true, friday: true },
  { date: "2026-12-18", dte: 105, monthly: true, friday: true },
];

export function dteBand(dte) {
  if (dte < POLICY_WINDOW.min) return "short";
  if (dte > POLICY_WINDOW.max) return "long";
  return "target";
}

/**
 * Earnings treatment for a selected expiry.
 * A predicted date is never treated as a confirmed no-overlap date.
 * A missing date is never invented.
 */
export function earningsConflict(earnings, dte) {
  if (!earnings || earnings.quality === "unavailable" || !earnings.date) {
    return { ...EARNINGS_CONFLICT.unavailable, date: null, quality: "unavailable", daysTo: null };
  }
  if (earnings.quality === "predicted") {
    return {
      ...EARNINGS_CONFLICT.predicted,
      date: earnings.date,
      quality: "predicted",
      daysTo: earnings.daysTo,
    };
  }
  if (earnings.past || (Number.isFinite(earnings.daysTo) && earnings.daysTo < 0)) {
    return {
      ...EARNINGS_CONFLICT.clear,
      date: earnings.date,
      quality: earnings.quality,
      daysTo: earnings.daysTo,
    };
  }
  const inside = Number.isFinite(earnings.daysTo) && Number.isFinite(dte) && earnings.daysTo <= dte;
  const base = !inside
    ? EARNINGS_CONFLICT.clear
    : earnings.quality === "confirmed"
      ? EARNINGS_CONFLICT.inside
      : { state: "inside", label: "Expected earnings date falls inside selected time window" };
  return {
    ...base,
    date: earnings.date,
    quality: earnings.quality,
    daysTo: earnings.daysTo,
  };
}

export function availableExpirations() {
  return LISTED_EXPIRIES.map((e) => ({
    ...e,
    band: dteBand(e.dte),
    label: `${e.friday ? "Fri" : ""} ${e.date.slice(8, 10)}/${e.date.slice(5, 7)} · ${e.dte} DTE`.trim(),
  }));
}

export function defaultExpiryDate() {
  const listed = availableExpirations();
  return (listed.find((e) => e.band === "target" && e.monthly) || listed.find((e) => e.band === "target") || listed[0]).date;
}

export function weeklyLabel(strategy, family, debit) {
  if (strategy === "Cash-secured put") return "Cash yield/week";
  if (strategy === "Covered call" || strategy === "Covered-call overlay") return "Call premium yield/week";
  if (strategy === "Bull put spread" || strategy === "Iron condor") return "Return on risk/week";
  if (strategy === "Protective put" || strategy === "Collar") return "Hedge cost/week";
  if (debit) return "Debit / max risk";
  return "Return on risk/week";
}

export function weeklyEconomics({ strategy, family, premiumValue, dte, definedRiskCap, debit, legs }) {
  const label = weeklyLabel(strategy, family, debit);
  const extra = strategy === "Covered call"
    ? { extraLabel: "Call-away return/week", extraValue: premiumValue == null || !dte ? "Unavailable" : `${((0.049 * 7) / dte * 100).toFixed(2)}%` }
    : null;
  if (premiumValue == null || !Number.isFinite(premiumValue) || !dte) {
    return { label, value: "Unavailable", weeklyPct: null, ...extra };
  }
  const strike = legs?.[0]?.strike;
  let weeklyPct = null;
  if (strategy === "Cash-secured put" && strike) {
    weeklyPct = (premiumValue / strike) * 7 / dte * 100;
  } else if ((strategy === "Covered call" || strategy === "Covered-call overlay") && strike) {
    weeklyPct = (premiumValue / strike) * 7 / dte * 100;
  } else if ((strategy === "Bull put spread" || strategy === "Iron condor") && definedRiskCap) {
    weeklyPct = ((premiumValue * 100) / definedRiskCap) * 7 / dte * 100;
  } else if (strategy === "Protective put" || strategy === "Collar") {
    weeklyPct = (premiumValue / (strike || 100)) * 7 / dte * 100;
  } else if (debit && definedRiskCap) {
    return { label, value: `${premiumValue.toFixed(2)} / ${(definedRiskCap / 100).toFixed(2)}`, weeklyPct: null, ...extra };
  } else if (debit) {
    return { label, value: `$${premiumValue.toFixed(2)} debit`, weeklyPct: null, ...extra };
  }
  if (weeklyPct == null) return { label, value: "Unavailable", weeklyPct: null, ...extra };
  return { label, value: `${weeklyPct.toFixed(2)}%`, weeklyPct, ...extra };
}

function hashSeed(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Mock historical trade outcomes. Not connected to the live backtest. */
export function historicalTrades({ ticker, strategy, sampleSize, avgRor, winRate, confirmedRegime }) {
  const n = Math.max(8, Number(sampleSize) || 18);
  const rng = mulberry32(hashSeed(`${ticker}:${strategy}:trades`));
  const regimes = ["Quiet Range", "Bullish Trend", "Bearish Trend", "Volatile"];
  const out = [];
  let cum = 0;
  const start = new Date(Date.UTC(2024, 2, 8));
  for (let i = 0; i < n; i += 1) {
    const win = rng() < ((winRate || 60) / 100);
    const mag = Math.abs((avgRor || 0.04) + (rng() - 0.45) * 0.12);
    const ror = win ? mag : -mag * (0.7 + rng() * 1.1);
    cum += ror;
    const d = new Date(start.getTime());
    d.setUTCDate(d.getUTCDate() + i * 21);
    const regime = rng() < 0.45 ? confirmedRegime : regimes[Math.floor(rng() * regimes.length)];
    out.push({
      i: i + 1,
      date: d.toISOString().slice(0, 10),
      ror: +ror.toFixed(3),
      cumulative: +cum.toFixed(3),
      regime,
      currentRegime: regime === confirmedRegime,
    });
  }
  return out;
}

export function deriveWorkspaceDecision(ticker, expiryDate, selectedRow) {
  const listed = availableExpirations();
  const exp = listed.find((e) => e.date === expiryDate) || listed.find((e) => e.date === defaultExpiryDate());
  const dte = exp?.dte ?? 42;
  const conflict = earningsConflict(ticker.earnings, dte);
  const quote = ticker.optionsState;
  const basis = selectedRow?.basis || quote;
  const data = dataLayerOf(basis);
  const event = eventLayerOf(conflict);
  const action = actionLayerOf(data.key);
  const screen = screenFromLayers(data.key, event.key);
  const detail = reasonForScreen(data.key, event.key);
  return {
    conflict,
    dte,
    expiry: exp?.date || expiryDate,
    state: screen,
    headline: SCREEN[screen].headline,
    detail,
    layers: { data, event, action },
  };
}

const REGIME_SAMPLE_FLOOR = 20;

const REGIME_CONTEXT_COLS = [
  { key: "Quiet Range", label: "Quiet" },
  { key: "Bullish Trend", label: "Bullish" },
  { key: "Bearish Trend", label: "Bearish" },
  { key: "Volatile", label: "Volatile" },
];

function showAssignment(strategy) {
  return strategy === "Cash-secured put" || strategy === "Covered call" || strategy === "Covered-call overlay";
}

function regimeContextColumns(tmpl, confirmedState) {
  const by = tmpl.regimeByState || {};
  return REGIME_CONTEXT_COLS.map(({ key, label }) => {
    const src = by[key] || (key === confirmedState
      ? {
        win_rate: tmpl.regime.win_rate,
        avg_return_on_risk: tmpl.regime.avg_return_on_risk,
        sample_size: tmpl.regime.sample_size,
        assignment_rate: tmpl.regime.assignment_rate,
        max_loss: tmpl.historical.max_loss,
      }
      : { sample_size: null });
    const n = src.sample_size;
    const insufficient = n == null || n < REGIME_SAMPLE_FLOOR;
    return {
      key,
      label,
      current: key === confirmedState,
      sample_size: n,
      insufficient,
      win_rate: insufficient ? null : (src.win_rate ?? null),
      avg_return_on_risk: insufficient ? null : (src.avg_return_on_risk ?? null),
      assignment_rate: !showAssignment(tmpl.strategy) || insufficient ? null : (src.assignment_rate ?? null),
      assignmentSupported: showAssignment(tmpl.strategy),
      max_loss: src.max_loss === undefined ? null : src.max_loss,
    };
  });
}

function money(n, debit) {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = `$${Math.abs(n).toFixed(2)}`;
  return debit ? `${abs} debit` : abs;
}

function scale(base, dte, ref = 42) {
  return +(base * (0.42 + 0.58 * (dte / ref))).toFixed(2);
}

/**
 * Field names mirror existing backtest / regime-fit outputs
 * (bt.win_rate, bt.avg_return_on_risk, bt.sample_size, bt.profit_factor,
 * bt.max_loss, regime_fit, bt_insufficient). Nothing is fetched.
 */
const STRATEGY_TEMPLATES = {
  ULTA: [
    {
      family: "income", strategy: "Cash-secured put",
      at: () => true,
      structure: (e) => `Short ${e.dte <= 21 ? 415 : 405} P`,
      legs: (e) => [{ side: "Short", right: "P", strike: e.dte <= 21 ? 415 : 405 }],
      width: "Cash-secured · strike risk",
      definedRiskCap: null,
      premium: (e) => scale(6.85, e.dte),
      debit: false,
      metric: (e, prem) => `Yield ${((prem / 405) * 100).toFixed(2)}% · Δ 0.24`,
      basis: "observed",
      historical: { win_rate: 71.2, avg_return_on_risk: 0.042, sample_size: 38, profit_factor: 1.84, max_loss: 4050, assignment_rate: 21.4 },
      regime: { regime_fit: "available", win_rate: 76.0, avg_return_on_risk: 0.051, sample_size: 22, assignment_rate: 18.2 },
      regimeByState: {
        "Quiet Range": { win_rate: 76.0, avg_return_on_risk: 0.051, sample_size: 22, assignment_rate: 18.2, max_loss: 4050 },
        "Bullish Trend": { win_rate: 74.1, avg_return_on_risk: 0.044, sample_size: 28, assignment_rate: 22.0, max_loss: 4050 },
        "Bearish Trend": { win_rate: null, avg_return_on_risk: null, sample_size: 9, assignment_rate: null, max_loss: 4050 },
        Volatile: { win_rate: null, avg_return_on_risk: null, sample_size: 14, assignment_rate: null, max_loss: 4050 },
      },
    },
    {
      family: "income", strategy: "Bull put spread",
      at: (e) => e.dte >= 14,
      structure: () => "Short 405 P / Long 390 P",
      legs: () => [{ side: "Short", right: "P", strike: 405 }, { side: "Long", right: "P", strike: 390 }],
      width: "15-wide put spread",
      definedRiskCap: 1190,
      premium: (e) => scale(3.10, e.dte),
      debit: false,
      metric: (e, prem) => `Max loss $11.90 · credit ${prem.toFixed(2)}`,
      basis: "observed",
      historical: { win_rate: 74.4, avg_return_on_risk: 0.186, sample_size: 36, profit_factor: 2.11, max_loss: 1190 },
      regime: { regime_fit: "available", win_rate: 79.2, avg_return_on_risk: 0.214, sample_size: 24 },
      regimeByState: {
        "Quiet Range": { win_rate: 79.2, avg_return_on_risk: 0.214, sample_size: 24, max_loss: 1190 },
        "Bullish Trend": { win_rate: 71.8, avg_return_on_risk: 0.168, sample_size: 21, max_loss: 1190 },
        "Bearish Trend": { win_rate: null, avg_return_on_risk: null, sample_size: 8, max_loss: 1190 },
        Volatile: { win_rate: 68.0, avg_return_on_risk: 0.142, sample_size: 20, max_loss: 1190 },
      },
    },
    {
      family: "income", strategy: "Covered call",
      at: () => true,
      structure: (e) => `Short ${e.dte <= 21 ? 445 : 450} C vs 100 sh`,
      legs: (e) => [{ side: "Short", right: "C", strike: e.dte <= 21 ? 445 : 450 }],
      width: "Covered · 100-share overlay",
      definedRiskCap: null,
      premium: (e) => scale(4.20, e.dte, 14),
      debit: false,
      metric: () => "Called-away return 4.9%",
      basis: "observed",
      historical: { win_rate: 68.1, avg_return_on_risk: 0.031, sample_size: 29, profit_factor: 1.46, max_loss: null, assignment_rate: 24.1 },
      regime: { regime_fit: "limited", win_rate: 70.0, avg_return_on_risk: 0.028, sample_size: 16, assignment_rate: 19.4 },
      regimeByState: {
        "Quiet Range": { win_rate: null, avg_return_on_risk: null, sample_size: 16, assignment_rate: null, max_loss: null },
        "Bullish Trend": { win_rate: 72.4, avg_return_on_risk: 0.036, sample_size: 21, assignment_rate: 26.0, max_loss: null },
        "Bearish Trend": { win_rate: null, avg_return_on_risk: null, sample_size: 7, assignment_rate: null, max_loss: null },
        Volatile: { win_rate: null, avg_return_on_risk: null, sample_size: 11, assignment_rate: null, max_loss: null },
      },
    },
    {
      family: "income", strategy: "Iron condor",
      at: (e) => e.dte >= 21,
      structure: () => "390/405 P · 450/465 C",
      legs: () => [
        { side: "Long", right: "P", strike: 390 }, { side: "Short", right: "P", strike: 405 },
        { side: "Short", right: "C", strike: 450 }, { side: "Long", right: "C", strike: 465 },
      ],
      width: "15-wide / 15-wide",
      definedRiskCap: 1005,
      premium: (e) => scale(4.95, e.dte),
      debit: false,
      metric: () => "Max loss $10.05",
      basis: "observed",
      historical: { win_rate: 69.4, avg_return_on_risk: 0.162, sample_size: 24, profit_factor: 1.72, max_loss: 1005 },
      regime: { regime_fit: "available", win_rate: 73.8, avg_return_on_risk: 0.188, sample_size: 21 },
      regimeByState: {
        "Quiet Range": { win_rate: 73.8, avg_return_on_risk: 0.188, sample_size: 21, max_loss: 1005 },
        "Bullish Trend": { win_rate: 66.0, avg_return_on_risk: 0.151, sample_size: 20, max_loss: 1005 },
        "Bearish Trend": { win_rate: null, avg_return_on_risk: null, sample_size: 7, max_loss: 1005 },
        Volatile: { win_rate: null, avg_return_on_risk: null, sample_size: 11, max_loss: 1005 },
      },
    },
    {
      family: "directional", strategy: "Call spread",
      at: (e) => e.dte >= 21,
      structure: () => "Long 435 C / Short 455 C",
      legs: () => [{ side: "Long", right: "C", strike: 435 }, { side: "Short", right: "C", strike: 455 }],
      width: "20-wide call spread",
      definedRiskCap: 740,
      premium: (e) => scale(7.40, e.dte),
      debit: true,
      metric: () => "Max gain $12.60",
      basis: "observed",
      historical: { win_rate: 54.0, avg_return_on_risk: 0.092, sample_size: 25, profit_factor: 1.21, max_loss: 740 },
      regime: { regime_fit: "limited", win_rate: 48.0, avg_return_on_risk: 0.041, sample_size: 12 },
    },
    {
      family: "protection", strategy: "Protective put",
      at: (e) => e.dte >= 21,
      structure: () => "Long 400 P vs 100 sh",
      legs: () => [{ side: "Long", right: "P", strike: 400 }],
      width: "Protective · 100-share cover",
      definedRiskCap: null,
      premium: (e) => scale(8.40, e.dte),
      debit: true,
      metric: () => "Floor −7.3% · cost of cover",
      basis: "observed",
      historical: { win_rate: 41.2, avg_return_on_risk: -0.018, sample_size: 18, profit_factor: null, max_loss: null },
      regime: { regime_fit: "limited", win_rate: null, avg_return_on_risk: null, sample_size: 11 },
      regimeByState: {
        "Quiet Range": { win_rate: null, avg_return_on_risk: null, sample_size: 11, max_loss: null },
        "Bullish Trend": { win_rate: null, avg_return_on_risk: null, sample_size: 8, max_loss: null },
        "Bearish Trend": { win_rate: 52.0, avg_return_on_risk: 0.021, sample_size: 20, max_loss: null },
        Volatile: { win_rate: null, avg_return_on_risk: null, sample_size: 6, max_loss: null },
      },
    },
    {
      family: "protection", strategy: "Collar",
      at: (e) => e.dte >= 42,
      structure: () => "Long 400 P / Short 470 C",
      legs: () => [{ side: "Long", right: "P", strike: 400 }, { side: "Short", right: "C", strike: 470 }],
      width: "Collar · 70-pt cap",
      definedRiskCap: null,
      premium: (e) => scale(0.35, e.dte, 105),
      debit: false,
      metric: () => "Floor −7.3%",
      basis: "observed",
      historical: { win_rate: 62.5, avg_return_on_risk: 0.011, sample_size: 16, profit_factor: null, max_loss: null },
      regime: { regime_fit: "limited", win_rate: 64.0, avg_return_on_risk: 0.009, sample_size: 9 },
      regimeByState: {
        "Quiet Range": { win_rate: null, avg_return_on_risk: null, sample_size: 9, max_loss: null },
        "Bullish Trend": { win_rate: null, avg_return_on_risk: null, sample_size: 12, max_loss: null },
        "Bearish Trend": { win_rate: 66.0, avg_return_on_risk: 0.019, sample_size: 21, max_loss: null },
        Volatile: { win_rate: null, avg_return_on_risk: null, sample_size: 5, max_loss: null },
      },
    },
    {
      family: "volatility", strategy: "Calendar",
      at: (e) => e.dte <= 21,
      structure: () => "430 C calendar",
      legs: () => [{ side: "Short", right: "C", strike: 430, note: "front" }, { side: "Long", right: "C", strike: 430, note: "back 2026-10-16" }],
      width: "Same-strike calendar",
      definedRiskCap: 210,
      premium: () => 2.10,
      debit: true,
      metric: () => "Indicative — modelled estimate",
      basis: "modelled",
      historical: { win_rate: 57.0, avg_return_on_risk: 0.064, sample_size: 14, profit_factor: null, max_loss: 210 },
      regime: { regime_fit: "differs", win_rate: null, avg_return_on_risk: null, sample_size: 6 },
    },
    {
      family: "repair", strategy: "Covered-call overlay",
      at: (e) => e.dte >= 21,
      structure: () => "Short 455 C vs 100 sh",
      legs: () => [{ side: "Short", right: "C", strike: 455 }],
      width: "Overlay on existing shares",
      definedRiskCap: null,
      premium: (e) => scale(3.05, e.dte),
      debit: false,
      metric: () => "Offsets 0.7% of basis",
      basis: "observed",
      historical: { win_rate: 66.0, avg_return_on_risk: 0.022, sample_size: 21, profit_factor: 1.33, max_loss: null },
      regime: { regime_fit: "available", win_rate: 69.0, avg_return_on_risk: 0.024, sample_size: 15 },
    },
  ],
  AAPL: [
    {
      family: "income", strategy: "Cash-secured put",
      at: () => true,
      structure: () => "Short 230 P",
      legs: () => [{ side: "Short", right: "P", strike: 230 }],
      width: "Cash-secured · strike risk",
      definedRiskCap: null,
      premium: (e) => scale(4.15, e.dte),
      debit: false,
      metric: () => "Indicative — partial quote",
      basis: "partial",
      historical: { win_rate: 73.8, avg_return_on_risk: 0.038, sample_size: 44, profit_factor: 1.91, max_loss: 2300 },
      regime: { regime_fit: "available", win_rate: 71.4, avg_return_on_risk: 0.035, sample_size: 28 },
    },
    {
      family: "income", strategy: "Bull put spread",
      at: (e) => e.dte >= 14,
      structure: () => "Short 230 P / Long 220 P",
      legs: () => [{ side: "Short", right: "P", strike: 230 }, { side: "Long", right: "P", strike: 220 }],
      width: "10-wide put spread",
      definedRiskCap: 795,
      premium: (e) => scale(2.05, e.dte),
      debit: false,
      metric: () => "Indicative — partial quote",
      basis: "partial",
      historical: { win_rate: 76.1, avg_return_on_risk: 0.171, sample_size: 41, profit_factor: 2.04, max_loss: 795 },
      regime: { regime_fit: "available", win_rate: 74.0, avg_return_on_risk: 0.166, sample_size: 26 },
    },
    {
      family: "income", strategy: "Covered call",
      at: () => true,
      structure: () => "Short 255 C vs 100 sh",
      legs: () => [{ side: "Short", right: "C", strike: 255 }],
      width: "Covered · 100-share overlay",
      definedRiskCap: null,
      premium: (e) => scale(3.60, e.dte),
      debit: false,
      metric: () => "Called-away return 7.3%",
      basis: "observed",
      historical: { win_rate: 70.5, avg_return_on_risk: 0.029, sample_size: 33, profit_factor: 1.55, max_loss: null },
      regime: { regime_fit: "limited", win_rate: 67.0, avg_return_on_risk: 0.025, sample_size: 19 },
    },
    {
      family: "directional", strategy: "Diagonal",
      at: (e) => e.dte >= 35,
      structure: () => "Long Jan 230 C / Short Oct 255 C",
      legs: () => [{ side: "Long", right: "C", strike: 230, note: "2027-01-15" }, { side: "Short", right: "C", strike: 255 }],
      width: "Diagonal · 25-pt short vs LEAP",
      definedRiskCap: null,
      premium: () => 18.40,
      debit: true,
      metric: () => "Indicative — partial quote",
      basis: "partial",
      historical: { win_rate: 58.0, avg_return_on_risk: 0.071, sample_size: 12, profit_factor: null, max_loss: null },
      regime: { regime_fit: "limited", win_rate: null, avg_return_on_risk: null, sample_size: 7 },
    },
    {
      family: "protection", strategy: "Protective put",
      at: (e) => e.dte >= 21,
      structure: () => "Long 225 P vs 100 sh",
      legs: () => [{ side: "Long", right: "P", strike: 225 }],
      width: "Protective · 100-share cover",
      definedRiskCap: null,
      premium: (e) => scale(7.95, e.dte, 105),
      debit: true,
      metric: () => "Floor −6.7% · cost 3.3%",
      basis: "observed",
      historical: { win_rate: 39.0, avg_return_on_risk: -0.021, sample_size: 17, profit_factor: null, max_loss: null },
      regime: { regime_fit: "limited", win_rate: null, avg_return_on_risk: null, sample_size: 8 },
    },
    {
      family: "volatility", strategy: "Strangle",
      at: (e) => e.dte >= 21,
      structure: () => "Short 225 P / Short 260 C",
      legs: () => [{ side: "Short", right: "P", strike: 225 }, { side: "Short", right: "C", strike: 260 }],
      width: "Short strangle · undefined risk",
      definedRiskCap: null,
      premium: (e) => scale(6.30, e.dte),
      debit: false,
      metric: () => "Indicative — partial quote",
      basis: "partial",
      historical: { win_rate: 64.0, avg_return_on_risk: 0.088, sample_size: 22, profit_factor: 1.39, max_loss: null },
      regime: { regime_fit: "differs", win_rate: 51.0, avg_return_on_risk: 0.012, sample_size: 9 },
    },
    {
      family: "repair", strategy: "Roll out",
      at: (e) => e.dte >= 49,
      structure: () => "Oct 255 C → later 260 C",
      legs: () => [{ side: "Buy to close", right: "C", strike: 255 }, { side: "Short", right: "C", strike: 260 }],
      width: "Roll · +5 strike",
      definedRiskCap: null,
      premium: () => 1.85,
      debit: false,
      metric: () => "Adds days, 5 pts strike",
      basis: "observed",
      historical: { win_rate: 61.0, avg_return_on_risk: 0.014, sample_size: 15, profit_factor: null, max_loss: null },
      regime: { regime_fit: "limited", win_rate: null, avg_return_on_risk: null, sample_size: 6 },
    },
  ],
  AVAV: [
    {
      family: "income", strategy: "Cash-secured put",
      at: () => true,
      structure: () => "Short 170 P",
      legs: () => [{ side: "Short", right: "P", strike: 170 }],
      width: "Cash-secured · strike risk",
      definedRiskCap: null,
      premium: () => null,
      debit: false,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 58.0, avg_return_on_risk: 0.055, sample_size: 19, profit_factor: 1.12, max_loss: 1700 },
      regime: { regime_fit: "differs", win_rate: null, avg_return_on_risk: null, sample_size: 5 },
    },
    {
      family: "income", strategy: "Bull put spread",
      at: (e) => e.dte >= 14,
      structure: () => "Short 170 P / Long 155 P",
      legs: () => [{ side: "Short", right: "P", strike: 170 }, { side: "Long", right: "P", strike: 155 }],
      width: "15-wide put spread",
      definedRiskCap: 1500,
      premium: () => null,
      debit: false,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 61.0, avg_return_on_risk: 0.142, sample_size: 17, profit_factor: 1.28, max_loss: 1500 },
      regime: { regime_fit: "differs", win_rate: null, avg_return_on_risk: null, sample_size: 4 },
    },
    {
      family: "income", strategy: "Iron condor",
      at: (e) => e.dte >= 21,
      structure: () => "155/170 P · 210/225 C",
      legs: () => [
        { side: "Long", right: "P", strike: 155 }, { side: "Short", right: "P", strike: 170 },
        { side: "Short", right: "C", strike: 210 }, { side: "Long", right: "C", strike: 225 },
      ],
      width: "15-wide / 15-wide",
      definedRiskCap: 1500,
      premium: () => null,
      debit: false,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 55.0, avg_return_on_risk: 0.101, sample_size: 11, profit_factor: 1.05, max_loss: 1500 },
      regime: { regime_fit: "differs", win_rate: null, avg_return_on_risk: null, sample_size: 3 },
    },
    {
      family: "directional", strategy: "Put spread",
      at: (e) => e.dte >= 21,
      structure: () => "Long 185 P / Short 170 P",
      legs: () => [{ side: "Long", right: "P", strike: 185 }, { side: "Short", right: "P", strike: 170 }],
      width: "15-wide put spread",
      definedRiskCap: 1500,
      premium: () => null,
      debit: true,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 52.0, avg_return_on_risk: 0.088, sample_size: 14, profit_factor: 1.09, max_loss: 1500 },
      regime: { regime_fit: "available", win_rate: null, avg_return_on_risk: null, sample_size: 6 },
    },
    {
      family: "protection", strategy: "Protective put",
      at: (e) => e.dte >= 21,
      structure: () => "Long 175 P vs 100 sh",
      legs: () => [{ side: "Long", right: "P", strike: 175 }],
      width: "Protective · 100-share cover",
      definedRiskCap: null,
      premium: () => null,
      debit: true,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 46.0, avg_return_on_risk: -0.009, sample_size: 13, profit_factor: null, max_loss: null },
      regime: { regime_fit: "available", win_rate: 51.0, avg_return_on_risk: 0.004, sample_size: 21 },
    },
    {
      family: "volatility", strategy: "Straddle",
      at: (e) => e.dte >= 21,
      structure: () => "Long 190 P / Long 190 C",
      legs: () => [{ side: "Long", right: "P", strike: 190 }, { side: "Long", right: "C", strike: 190 }],
      width: "Long straddle · ATM",
      definedRiskCap: null,
      premium: () => null,
      debit: true,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 49.0, avg_return_on_risk: 0.077, sample_size: 10, profit_factor: null, max_loss: null },
      regime: { regime_fit: "available", win_rate: null, avg_return_on_risk: null, sample_size: 8 },
    },
    {
      family: "repair", strategy: "Collar",
      at: (e) => e.dte >= 42,
      structure: () => "Long 175 P / Short 215 C",
      legs: () => [{ side: "Long", right: "P", strike: 175 }, { side: "Short", right: "C", strike: 215 }],
      width: "Collar · 40-pt cap",
      definedRiskCap: null,
      premium: () => null,
      debit: false,
      metric: () => "Timestamp unverifiable",
      basis: "unverified",
      historical: { win_rate: 57.0, avg_return_on_risk: 0.008, sample_size: 9, profit_factor: null, max_loss: null },
      regime: { regime_fit: "available", win_rate: null, avg_return_on_risk: null, sample_size: 5 },
    },
  ],
};

function layerBundle({ basis, conflict }) {
  const data = dataLayerOf(basis);
  const event = eventLayerOf(conflict);
  const action = actionLayerOf(data.key);
  const screen = screenFromLayers(data.key, event.key);
  return { data, event, action, screen };
}

function readinessFrom(basis, conflict, layers) {
  const flags = [];
  if (basis === "partial") flags.push(REASON.quoteFields);
  if (basis === "unverified") flags.push(REASON.timestamp);
  if (basis === "unavailable") flags.push(REASON.noData);
  if (basis === "modelled" || basis === "lastPrice") flags.push(REASON.indicative);
  if (layers.action.key === "unavailable") flags.push(BASKET.unavailable);
  else if (layers.screen === "flag") flags.push(layers.event.text);

  const pricingMethod = {
    observed: "Observed quote",
    partial: REASON.quoteFields,
    modelled: REASON.indicative,
    lastPrice: REASON.indicative,
    unverified: REASON.timestamp,
    unavailable: "Pricing unavailable",
  }[basis] || REASON.indicative;

  return {
    structureValid: basis !== "unavailable",
    quoteCompleteness: basis === "observed" ? "Observed" : basis === "partial" ? "Incomplete" : basis === "unverified" ? "Unavailable" : "Incomplete",
    timestampQuality: basis === "unverified" ? "Unavailable" : basis === "observed" || basis === "partial" ? "Observed" : "Incomplete",
    pricingMethod,
    earningsConflict: conflict.label,
    liquidityState: layers.action.text,
    blockers: [...new Set(flags)],
  };
}

function buildRow(ticker, tmpl, expiry, earnings, regime) {
  const conflict = earningsConflict(earnings, expiry.dte);
  const prem = tmpl.premium(expiry);
  const layers = layerBundle({ basis: tmpl.basis, conflict });
  const state = layers.screen;
  const hist = tmpl.historical;
  const reg = tmpl.regime;
  const insufficient = !reg.sample_size || reg.sample_size < REGIME_SAMPLE_FLOOR;
  const legs = tmpl.legs(expiry);
  const weekly = weeklyEconomics({
    strategy: tmpl.strategy,
    family: tmpl.family,
    premiumValue: prem,
    dte: expiry.dte,
    definedRiskCap: tmpl.definedRiskCap,
    debit: tmpl.debit,
    legs,
  });
  const trades = historicalTrades({
    ticker,
    strategy: tmpl.strategy,
    sampleSize: hist.sample_size,
    avgRor: hist.avg_return_on_risk,
    winRate: hist.win_rate,
    confirmedRegime: regime.confirmedState,
  });
  return {
    id: `${ticker}-${tmpl.strategy}-${expiry.date}`,
    ticker,
    family: tmpl.family,
    strategy: tmpl.strategy,
    expiry: expiry.date,
    dte: expiry.dte,
    band: expiry.band,
    monthly: expiry.monthly,
    structure: tmpl.structure(expiry),
    legs,
    width: tmpl.width,
    basis: tmpl.basis,
    premium: money(prem, tmpl.debit),
    premiumValue: prem,
    weekly,
    metric: tmpl.metric(expiry, prem ?? 0),
    state,
    layers,
    conflict,
    beyondP75: regime.sojournP75 != null && expiry.dte > regime.sojournP75,
    evidence: {
      family: STRATEGY_FAMILIES.find((f) => f.id === tmpl.family)?.label || tmpl.family,
      legs,
      expiry: expiry.date,
      dte: expiry.dte,
      width: tmpl.width,
      basis: tmpl.basis,
      state,
      weekly,
      historical: {
        win_rate: hist.win_rate,
        avg_return_on_risk: hist.avg_return_on_risk,
        sample_size: hist.sample_size,
        profit_factor: hist.profit_factor,
        max_loss: hist.max_loss,
        defined_risk_cap: tmpl.definedRiskCap,
        assignment_rate: hist.assignment_rate ?? null,
        trades,
      },
      regime: {
        confirmedState: regime.confirmedState,
        confidence: regime.confidence,
        regime_fit: reg.regime_fit,
        win_rate: insufficient ? null : reg.win_rate,
        avg_return_on_risk: insufficient ? null : reg.avg_return_on_risk,
        sample_size: reg.sample_size,
        assignment_rate: insufficient ? null : (reg.assignment_rate ?? null),
        insufficient,
      },
      regimeContext: regimeContextColumns(tmpl, regime.confirmedState),
      readiness: readinessFrom(tmpl.basis, conflict, layers),
    },
  };
}

export function strategyRows(ticker, expiryDate) {
  const t = BY_TICKER[ticker];
  const templates = STRATEGY_TEMPLATES[ticker] || [];
  if (!t) return [];
  const listed = availableExpirations();
  const wanted = expiryDate ? listed.filter((e) => e.date === expiryDate) : listed;
  const rows = [];
  wanted.forEach((e) => {
    templates.forEach((tmpl) => {
      if (tmpl.at(e)) rows.push(buildRow(ticker, tmpl, e, t.earnings, t.regime));
    });
  });
  return rows;
}

/* -------------------------------------------------------------- fundamentals */

const FUNDAMENTALS = {
  ULTA: {
    profile: {
      summary:
        "Ulta Beauty operates a specialty beauty retail chain across the United States, pairing prestige and mass cosmetics with in-store salon services and a loyalty programme that drives most of its transactions.",
      website: "ulta.com",
      employees: 55000,
      headquarters: "Bolingbrook, Illinois",
      executives: [
        { name: "Kecia Steelman", role: "President & Chief Executive Officer" },
        { name: "Paula Oyibo", role: "Chief Financial Officer" },
        { name: "Amiee Bayer-Thomas", role: "Chief Retail Officer" },
      ],
    },
    metrics: [
      { key: "revenue", label: "Revenue (TTM)", value: "$11.6B", peer: "$8.9B", better: "up" },
      { key: "ebitda", label: "EBITDA (TTM)", value: "$1.72B", peer: "$1.10B", better: "up" },
      { key: "grossMargin", label: "Gross margin", value: "38.9%", peer: "35.2%", better: "up" },
      { key: "operatingMargin", label: "Operating margin", value: "12.6%", peer: "9.4%", better: "up" },
      { key: "eps", label: "EPS (TTM)", value: "$25.84", peer: "$14.20", better: "up" },
      { key: "fcf", label: "Free cash flow", value: "$1.01B", peer: "$620M", better: "up" },
      { key: "debtEquity", label: "Debt / equity", value: "0.94", peer: "1.31", better: "up" },
      { key: "interestCoverage", label: "Interest coverage", value: "18.2×", peer: "9.8×", better: "up" },
      { key: "pe", label: "P/E", value: "16.7", peer: "19.4", better: "up" },
      { key: "forwardPe", label: "Forward P/E", value: "15.1", peer: "17.8", better: "up" },
      { key: "evEbitda", label: "EV / EBITDA", value: "10.4", peer: "12.1", better: "up" },
      { key: "peg", label: "PEG", value: "1.42", peer: "1.66", better: "up" },
    ],
    quarterly: [
      { period: "Q3 25", revenue: 2.53, eps: 5.14, margin: 12.1 },
      { period: "Q4 25", revenue: 3.49, eps: 8.46, margin: 14.8 },
      { period: "Q1 26", revenue: 2.85, eps: 6.70, margin: 12.9 },
      { period: "Q2 26", revenue: 2.67, eps: 5.54, margin: 11.7 },
    ],
    annual: [
      { period: "FY23", revenue: 10.21, eps: 24.01, margin: 15.0 },
      { period: "FY24", revenue: 11.21, eps: 26.03, margin: 14.1 },
      { period: "FY25", revenue: 11.30, eps: 25.34, margin: 13.0 },
      { period: "FY26e", revenue: 11.62, eps: 25.84, margin: 12.6 },
    ],
  },
  AAPL: {
    profile: {
      summary:
        "Apple designs and sells smartphones, personal computers, tablets, wearables and accessories, and operates a services segment spanning the App Store, advertising, payments, cloud storage and subscription media.",
      website: "apple.com",
      employees: 164000,
      headquarters: "Cupertino, California",
      executives: [
        { name: "Tim Cook", role: "Chief Executive Officer" },
        { name: "Kevan Parekh", role: "Chief Financial Officer" },
        { name: "Jeff Williams", role: "Chief Operating Officer" },
      ],
    },
    metrics: [
      { key: "revenue", label: "Revenue (TTM)", value: "$408B", peer: "$188B", better: "up" },
      { key: "ebitda", label: "EBITDA (TTM)", value: "$139B", peer: "$61B", better: "up" },
      { key: "grossMargin", label: "Gross margin", value: "46.8%", peer: "42.0%", better: "up" },
      { key: "operatingMargin", label: "Operating margin", value: "31.9%", peer: "24.6%", better: "up" },
      { key: "eps", label: "EPS (TTM)", value: "$7.42", peer: "$5.10", better: "up" },
      { key: "fcf", label: "Free cash flow", value: "$112B", peer: "$44B", better: "up" },
      { key: "debtEquity", label: "Debt / equity", value: "1.45", peer: "0.88", better: "down" },
      { key: "interestCoverage", label: "Interest coverage", value: "41.6×", peer: "22.3×", better: "up" },
      { key: "pe", label: "P/E", value: "32.5", peer: "28.1", better: "down" },
      { key: "forwardPe", label: "Forward P/E", value: "29.4", peer: "25.6", better: "down" },
      { key: "evEbitda", label: "EV / EBITDA", value: "23.8", peer: "19.2", better: "down" },
      { key: "peg", label: "PEG", value: "2.61", peer: "2.04", better: "down" },
    ],
    quarterly: [
      { period: "Q3 25", revenue: 94.9, eps: 1.64, margin: 30.1 },
      { period: "Q4 25", revenue: 124.3, eps: 2.41, margin: 34.2 },
      { period: "Q1 26", revenue: 95.4, eps: 1.71, margin: 31.0 },
      { period: "Q2 26", revenue: 93.7, eps: 1.66, margin: 30.4 },
    ],
    annual: [
      { period: "FY23", revenue: 383.3, eps: 6.13, margin: 29.8 },
      { period: "FY24", revenue: 391.0, eps: 6.75, margin: 31.5 },
      { period: "FY25", revenue: 401.2, eps: 7.11, margin: 31.7 },
      { period: "FY26e", revenue: 408.3, eps: 7.42, margin: 31.9 },
    ],
  },
  AVAV: {
    profile: {
      summary:
        "AeroVironment supplies unmanned aircraft systems, loitering munitions and related autonomy software, selling primarily to United States and allied defence customers under multi-year procurement programmes.",
      website: "avav.com",
      employees: 2600,
      headquarters: "Arlington, Virginia",
      executives: [
        { name: "Wahid Nawabi", role: "Chairman, President & Chief Executive Officer" },
        { name: "Kevin McDonnell", role: "Chief Financial Officer" },
        { name: "Trace Stevenson", role: "President, Autonomous Systems" },
      ],
    },
    metrics: [
      { key: "revenue", label: "Revenue (TTM)", value: "$1.94B", peer: "$3.10B", better: "down" },
      { key: "ebitda", label: "EBITDA (TTM)", value: "$248M", peer: "$430M", better: "down" },
      { key: "grossMargin", label: "Gross margin", value: "39.4%", peer: "31.8%", better: "up" },
      { key: "operatingMargin", label: "Operating margin", value: "8.1%", peer: "11.9%", better: "down" },
      { key: "eps", label: "EPS (TTM)", value: "$2.06", peer: "$4.44", better: "down" },
      { key: "fcf", label: "Free cash flow", value: "$96M", peer: "$310M", better: "down" },
      { key: "debtEquity", label: "Debt / equity", value: "0.41", peer: "0.77", better: "up" },
      { key: "interestCoverage", label: "Interest coverage", value: "6.4×", peer: "11.5×", better: "down" },
      { key: "pe", label: "P/E", value: "91.5", peer: "26.8", better: "down" },
      { key: "forwardPe", label: "Forward P/E", value: "58.2", peer: "23.9", better: "down" },
      { key: "evEbitda", label: "EV / EBITDA", value: "34.1", peer: "15.7", better: "down" },
      { key: "peg", label: "PEG", value: "3.18", peer: "1.72", better: "down" },
    ],
    quarterly: [
      { period: "Q3 25", revenue: 0.41, eps: 0.42, margin: 7.4 },
      { period: "Q4 25", revenue: 0.55, eps: 0.71, margin: 9.8 },
      { period: "Q1 26", revenue: 0.47, eps: 0.48, margin: 7.9 },
      { period: "Q2 26", revenue: 0.51, eps: 0.45, margin: 7.1 },
    ],
    annual: [
      { period: "FY23", revenue: 0.54, eps: 0.61, margin: 5.2 },
      { period: "FY24", revenue: 0.72, eps: 1.24, margin: 7.1 },
      { period: "FY25", revenue: 1.41, eps: 1.88, margin: 8.4 },
      { period: "FY26e", revenue: 1.94, eps: 2.06, margin: 8.1 },
    ],
  },
};

export function fundamentals(ticker) {
  return FUNDAMENTALS[ticker] || FUNDAMENTALS.ULTA;
}

const ANALYST = {
  ULTA: { median: 455, count: 22, low: 410, high: 510, asOf: "2026-08-28" },
  AAPL: { median: 248, count: 34, low: 210, high: 280, asOf: "2026-08-30" },
  AVAV: { median: 198, count: 9, low: 160, high: 240, asOf: "2026-08-22" },
};

export function analystConsensus(ticker) {
  return ANALYST[ticker] || null;
}

/* ------------------------------------------------------------------ earnings */

const EARNINGS = {
  ULTA: {
    estimates: [
      { period: "Q3 26", revenueEst: "$2.61B", epsEst: "$5.02", analysts: 22 },
      { period: "Q4 26", revenueEst: "$3.58B", epsEst: "$8.71", analysts: 21 },
    ],
    history: [
      { period: "Q2 26", epsEst: 5.31, epsActual: 5.54, surprisePct: 4.3, movePct: 3.1 },
      { period: "Q1 26", epsEst: 6.55, epsActual: 6.70, surprisePct: 2.3, movePct: -1.8 },
      { period: "Q4 25", epsEst: 8.20, epsActual: 8.46, surprisePct: 3.2, movePct: 5.4 },
      { period: "Q3 25", epsEst: 5.02, epsActual: 5.14, surprisePct: 2.4, movePct: -2.2 },
    ],
    treatment:
      "A 42-day tenor still contains the confirmed September print. A 14-day covered call also contains that print. Both show an event overlap on the income screen.",
  },
  AAPL: {
    estimates: [
      { period: "Q4 26", revenueEst: "$128.4B", epsEst: "$2.48", analysts: 34 },
      { period: "Q1 27", revenueEst: "$99.1B", epsEst: "$1.79", analysts: 31 },
    ],
    history: [
      { period: "Q2 26", epsEst: 1.61, epsActual: 1.66, surprisePct: 3.1, movePct: 1.4 },
      { period: "Q1 26", epsEst: 1.68, epsActual: 1.71, surprisePct: 1.8, movePct: -0.9 },
      { period: "Q4 25", epsEst: 2.36, epsActual: 2.41, surprisePct: 2.1, movePct: 2.8 },
      { period: "Q3 25", epsEst: 1.59, epsActual: 1.64, surprisePct: 3.1, movePct: -3.4 },
    ],
    treatment:
      "The expected date is 53 days out, so a 42-day tenor stays clear. The date is an estimate, not a company confirmation, so a later shift can pull the print inside the tenor.",
  },
  AVAV: {
    estimates: [
      { period: "Q3 26", revenueEst: "$0.54B", epsEst: "$0.51", analysts: 9 },
      { period: "Q4 26", revenueEst: "$0.62B", epsEst: "$0.68", analysts: 8 },
    ],
    history: [
      { period: "Q2 26", epsEst: 0.52, epsActual: 0.45, surprisePct: -13.5, movePct: -9.8 },
      { period: "Q1 26", epsEst: 0.44, epsActual: 0.48, surprisePct: 9.1, movePct: 6.2 },
      { period: "Q4 25", epsEst: 0.66, epsActual: 0.71, surprisePct: 7.6, movePct: 11.4 },
      { period: "Q3 25", epsEst: 0.47, epsActual: 0.42, surprisePct: -10.6, movePct: -14.1 },
    ],
    treatment:
      "The date is a model prediction from the historical reporting cadence, not a company or vendor confirmation. Earnings-risk sizing is unavailable while the date quality is predicted.",
  },
};

export function earnings(ticker) {
  return EARNINGS[ticker] || EARNINGS.ULTA;
}

/* --------------------------------------------------------------- ticker talk */

export const TALK_TOPICS = [
  "Earnings",
  "Analyst action",
  "Regulatory",
  "M&A",
  "Macro",
  "Industry",
  "Management",
  "Options activity",
  "Insider",
];

const TALK = {
  ULTA: [
    { id: "u1", date: "2026-09-04", source: "Company press release", headline: "Ulta Beauty schedules third-quarter results for September 18", topics: ["Earnings"], tone: "neutral", cluster: 1 },
    { id: "u2", date: "2026-09-03", source: "Broker note", headline: "Coverage resumed at overweight on loyalty-programme reacceleration", topics: ["Analyst action"], tone: "constructive", cluster: 3 },
    { id: "u3", date: "2026-09-02", source: "Trade press", headline: "Prestige beauty category grows mid-single digits in August scanner data", topics: ["Industry", "Macro"], tone: "constructive", cluster: 3 },
    { id: "u4", date: "2026-08-29", source: "Regulatory filing", headline: "Chief Merchandising Officer reports a planned sale under a 10b5-1 plan", topics: ["Insider", "Management"], tone: "neutral", cluster: 1 },
    { id: "u5", date: "2026-08-27", source: "Options tape", headline: "October 405 puts print roughly four times the twenty-day average volume", topics: ["Options activity"], tone: "neutral", cluster: 2 },
  ],
  AAPL: [
    { id: "a1", date: "2026-09-04", source: "Trade press", headline: "Supplier notes a two-week production pause at one assembly line", topics: ["Industry"], tone: "cautious", cluster: 4 },
    { id: "a2", date: "2026-09-03", source: "Broker note", headline: "Price target raised on services margin commentary", topics: ["Analyst action"], tone: "constructive", cluster: 5 },
    { id: "a3", date: "2026-09-02", source: "Regulatory", headline: "European regulator closes a routine inquiry with no enforcement action", topics: ["Regulatory"], tone: "neutral", cluster: 2 },
    { id: "a4", date: "2026-08-30", source: "Company press release", headline: "Board authorises an increase to the existing repurchase programme", topics: ["Management"], tone: "constructive", cluster: 5 },
    { id: "a5", date: "2026-08-28", source: "Trade press", headline: "China sell-through cools versus the year-ago launch week", topics: ["Industry", "Macro"], tone: "cautious", cluster: 4 },
  ],
  AVAV: [
    { id: "v1", date: "2026-09-04", source: "Trade press", headline: "Shares plunge after the company misses delivery estimates on a flagship programme", topics: ["Earnings", "Industry"], tone: "cautious", cluster: 6 },
    { id: "v2", date: "2026-09-03", source: "Broker note", headline: "Two brokers cut price targets citing programme timing", topics: ["Analyst action"], tone: "cautious", cluster: 6 },
    { id: "v3", date: "2026-09-01", source: "Government notice", headline: "Defence procurement schedule for the next fiscal year is delayed by one quarter", topics: ["Regulatory", "Macro"], tone: "cautious", cluster: 6 },
    { id: "v4", date: "2026-08-28", source: "Company press release", headline: "New autonomy software contract awarded by an allied ministry", topics: ["M&A", "Industry"], tone: "constructive", cluster: 2 },
    { id: "v5", date: "2026-08-26", source: "Options tape", headline: "Implied volatility on the October chain reaches the top decile of its one-year range", topics: ["Options activity"], tone: "neutral", cluster: 3 },
  ],
};

export function talkFeed(ticker) {
  return TALK[ticker] || [];
}

/* ------------------------------------------------- ownership and disclosures */

const OWNERSHIP = {
  ULTA: {
    holders: [
      { investorName: "Vanguard Group", positionValueUsd: 2.14e9, shares: 4.96e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "BlackRock", positionValueUsd: 1.72e9, shares: 3.98e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "State Street", positionValueUsd: 0.81e9, shares: 1.88e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "T. Rowe Price", positionValueUsd: 0.46e9, shares: 1.07e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "Geode Capital", positionValueUsd: 0.39e9, shares: 0.90e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
    ],
    insider: [
      { id: "u-f4-1", date: "2026-08-29", valueUsd: 1.84e6, shares: 4200, type: "Sell", insiderName: "Chief Merchandising Officer" },
      { id: "u-f4-2", date: "2026-08-14", valueUsd: 0.62e6, shares: 1450, type: "Sell", insiderName: "SVP Operations" },
      { id: "u-f4-3", date: "2026-07-31", valueUsd: 0.41e6, shares: 980, type: "Buy", insiderName: "Director" },
      { id: "u-f4-4", date: "2026-07-18", valueUsd: 0.28e6, shares: 660, type: "Buy", insiderName: "Director" },
    ],
    buyCount30d: 1,
    sellCount30d: 2,
    congress: [
      { id: "u-ptr-1", politicianName: "Representative A", party: "", chamber: "House", type: "Buy", amountBracket: "$15,001–$50,000", amountMidpointUsd: 32500, tradeDate: "2026-08-21", delayFlag: false, delayDays: 0 },
      { id: "u-ptr-2", politicianName: "Senator B", party: "", chamber: "Senate", type: "Sell", amountBracket: "$50,001–$100,000", amountMidpointUsd: 75000, tradeDate: "2026-08-06", delayFlag: true, delayDays: 41 },
    ],
    proposedSales: [
      { id: "u-144-1", date: "2026-08-12", seller: "Affiliate holder", shares: 9500, marketValueUsd: 4.1e6 },
      { id: "u-144-2", date: "2026-07-24", seller: "Affiliate holder", shares: 4200, marketValueUsd: 1.8e6 },
    ],
  },
  AAPL: {
    holders: [
      { investorName: "Vanguard Group", positionValueUsd: 3.12e11, shares: 1.42e9, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "BlackRock", positionValueUsd: 2.68e11, shares: 1.21e9, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "State Street", positionValueUsd: 1.18e11, shares: 0.54e9, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "Berkshire Hathaway", positionValueUsd: 0.72e11, shares: 0.33e9, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "Geode Capital", positionValueUsd: 0.28e11, shares: 0.13e9, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
    ],
    insider: [
      { id: "a-f4-1", date: "2026-09-02", valueUsd: 12.4e6, shares: 51000, type: "Sell", insiderName: "Chief Executive Officer" },
      { id: "a-f4-2", date: "2026-08-21", valueUsd: 4.10e6, shares: 17000, type: "Sell", insiderName: "General Counsel" },
      { id: "a-f4-3", date: "2026-08-08", valueUsd: 2.20e6, shares: 9200, type: "Sell", insiderName: "Chief Operating Officer" },
      { id: "a-f4-4", date: "2026-07-29", valueUsd: 0.86e6, shares: 3600, type: "Buy", insiderName: "Director" },
    ],
    buyCount30d: 1,
    sellCount30d: 3,
    congress: [
      { id: "a-ptr-1", politicianName: "Representative C", party: "", chamber: "House", type: "Buy", amountBracket: "$100,001–$250,000", amountMidpointUsd: 175000, tradeDate: "2026-08-27", delayFlag: false, delayDays: 0 },
      { id: "a-ptr-2", politicianName: "Representative C", party: "", chamber: "House", type: "Sell", amountBracket: "$15,001–$50,000", amountMidpointUsd: 32500, tradeDate: "2026-08-12", delayFlag: false, delayDays: 0 },
      { id: "a-ptr-3", politicianName: "Senator D", party: "", chamber: "Senate", type: "Buy", amountBracket: "$1,001–$15,000", amountMidpointUsd: 8000, tradeDate: "2026-07-30", delayFlag: true, delayDays: 36 },
    ],
    proposedSales: [
      { id: "a-144-1", date: "2026-09-01", seller: "Affiliate holder", shares: 120000, marketValueUsd: 28.9e6 },
      { id: "a-144-2", date: "2026-08-11", seller: "Affiliate holder", shares: 64000, marketValueUsd: 15.4e6 },
      { id: "a-144-3", date: "2026-07-22", seller: "Affiliate holder", shares: 31000, marketValueUsd: 7.3e6 },
    ],
  },
  AVAV: {
    holders: [
      { investorName: "BlackRock", positionValueUsd: 0.84e9, shares: 4.46e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "Vanguard Group", positionValueUsd: 0.71e9, shares: 3.77e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "Wellington Management", positionValueUsd: 0.22e9, shares: 1.17e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
      { investorName: "State Street", positionValueUsd: 0.18e9, shares: 0.96e6, newPositionUsd: 0, exitUsd: 0, periodOfReport: "2026-06-30" },
    ],
    insider: [
      { id: "v-f4-1", date: "2026-09-03", valueUsd: 0.94e6, shares: 4900, type: "Buy", insiderName: "Chief Executive Officer" },
      { id: "v-f4-2", date: "2026-08-25", valueUsd: 0.31e6, shares: 1600, type: "Buy", insiderName: "Director" },
      { id: "v-f4-3", date: "2026-08-19", valueUsd: 1.42e6, shares: 6800, type: "Sell", insiderName: "Chief Financial Officer" },
    ],
    buyCount30d: 2,
    sellCount30d: 1,
    congress: [
      { id: "v-ptr-1", politicianName: "Senator E", party: "", chamber: "Senate", type: "Buy", amountBracket: "$250,001–$500,000", amountMidpointUsd: 375000, tradeDate: "2026-08-18", delayFlag: false, delayDays: 0 },
    ],
    proposedSales: [
      { id: "v-144-1", date: "2026-08-20", seller: "Affiliate holder", shares: 22000, marketValueUsd: 4.3e6 },
    ],
  },
};

export function ownership(ticker) {
  return OWNERSHIP[ticker] || OWNERSHIP.ULTA;
}

export function tickerMock(ticker) {
  return BY_TICKER[ticker] || ULTA;
}
